import { useRef, type ReactNode } from 'react'
import type { TextLookupSelection } from '../data/vocabularyLookup'
import { useTextLookupSelection } from '../hooks/useTextLookupSelection'
import type { InterestValue, IssueItem, ItemState, VocabularyEntry } from '../types/daily'
import { topicLabel } from '../utils/labels'
import { SelectionLookupButton } from './SelectionLookupButton'

interface ArticleDetailProps {
  item: IssueItem
  state?: ItemState
  onSaved: (saved: boolean) => void
  onInterest: (interest: InterestValue) => void
  onVocabulary: (entry: VocabularyEntry) => void
  onLookupSelection?: (selection: TextLookupSelection) => void
  onDiscuss: () => void
}

const confidenceLabels = { low: '低', medium: '中', high: '高' }

const formatPublishedAt = (value: string, language: 'zh-CN' | 'en') => new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'zh-CN', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Singapore',
}).format(new Date(value))

function EvidenceSection({ label, tone, children }: { label: string; tone: string; children: ReactNode }) {
  return (
    <section className={`evidence evidence--${tone}`}>
      <h4>{label}</h4>
      {children}
    </section>
  )
}

function SourceLinks({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null
  return (
    <span className="evidence__sources">
      {urls.map((url, index) => (
        <a key={url} href={url} target="_blank" rel="noreferrer noopener">来源 {index + 1}</a>
      ))}
    </span>
  )
}

export function ArticleDetail({ item, state, onSaved, onInterest, onVocabulary, onLookupSelection, onDiscuss }: ArticleDetailProps) {
  const articleRef = useRef<HTMLElement>(null)
  const { selection, clear: clearSelection } = useTextLookupSelection(articleRef)
  const lookupEnabled = item.source.language === 'en' && Boolean(onLookupSelection)
  const lookup = () => {
    if (!selection || !onLookupSelection) return
    onLookupSelection(selection)
    clearSelection()
  }

  return (
    <article ref={articleRef} id={`article-${item.id}`} className="article-detail page-enter" aria-labelledby={`title-${item.id}`}>
      <header className="article-detail__header">
        <div className="article-detail__meta">
          <span>{item.source.name}</span>
          <span>{formatPublishedAt(item.source.publishedAt, item.source.language)}</span>
          <span>约 {item.estimatedMinutes} 分钟</span>
        </div>
        <h2 id={`title-${item.id}`} lang={item.source.language === 'en' ? 'en' : 'zh-CN'} data-lookup-text={lookupEnabled ? 'true' : undefined}>{item.title}</h2>
        <div className="topic-row">
          {item.topics.map((topic) => <span key={topic}>{topicLabel(topic)}</span>)}
          {item.featuredEnglish && <span className="topic-row__featured">英文重点阅读 · {item.languageAid?.cefrEstimate}</span>}
        </div>
      </header>

      <p className="article-detail__summary" lang={item.source.language === 'en' ? 'en' : 'zh-CN'} data-lookup-text={lookupEnabled ? 'true' : undefined}>{item.summary}</p>

      <section className="why-section">
        <p className="eyebrow">Why it matters · 为什么重要</p>
        <p lang={item.source.language === 'en' ? 'en' : 'zh-CN'} data-lookup-text={lookupEnabled ? 'true' : undefined}>{item.whyItMatters}</p>
      </section>

      <div className="evidence-grid">
        {item.facts.length > 0 && (
          <EvidenceSection label="已确认事实" tone="fact">
            <ul>{item.facts.map((fact, index) => <li key={`${item.id}-fact-${index}`}><p data-lookup-text={lookupEnabled ? 'true' : undefined}>{fact.statement}</p><SourceLinks urls={fact.sourceUrls} /></li>)}</ul>
          </EvidenceSection>
        )}
        {item.viewpoints.length > 0 && (
          <EvidenceSection label="来源观点" tone="viewpoint">
            <ul>{item.viewpoints.map((viewpoint, index) => <li key={`${item.id}-view-${index}`}><strong lang={lookupEnabled ? 'en' : undefined} data-lookup-text={lookupEnabled ? 'true' : undefined}>{viewpoint.attribution}</strong><p data-lookup-text={lookupEnabled ? 'true' : undefined}>{viewpoint.statement}</p><SourceLinks urls={viewpoint.sourceUrls} /></li>)}</ul>
          </EvidenceSection>
        )}
        {item.inferences.length > 0 && (
          <EvidenceSection label="分析推断" tone="inference">
            <ul>{item.inferences.map((inference, index) => <li key={`${item.id}-inference-${index}`}><span className="confidence">置信度：{confidenceLabels[inference.confidence]}</span><p data-lookup-text={lookupEnabled ? 'true' : undefined}>{inference.statement}</p><small data-lookup-text={lookupEnabled ? 'true' : undefined}>{inference.reasoning}</small></li>)}</ul>
          </EvidenceSection>
        )}
        {item.uncertainties.length > 0 && (
          <EvidenceSection label="仍不确定" tone="uncertainty">
            <ul>{item.uncertainties.map((uncertainty) => <li key={uncertainty} data-lookup-text={lookupEnabled ? 'true' : undefined}>{uncertainty}</li>)}</ul>
          </EvidenceSection>
        )}
      </div>

      {item.supportingSources.length > 0 && (
        <section className="supporting-sources">
          <h3>延伸与交叉来源</h3>
          <div>{item.supportingSources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer noopener">{source.name}</a>)}</div>
        </section>
      )}

      {item.languageAid && (
        <section className="language-lab">
          <div className="section-heading">
            <div><p className="eyebrow">Language notes</p><h3>英文阅读辅助</h3></div>
            <span>{item.languageAid.vocabulary.length} 个表达</span>
          </div>
          <div className="vocabulary-list">
            {item.languageAid.vocabulary.map((entry) => (
              <button key={entry.term} type="button" onClick={() => onVocabulary(entry)}>
                <span>{entry.term}</span><small>{entry.difficulty}</small>
              </button>
            ))}
          </div>
          {item.languageAid.sentenceNotes.map((note) => (
            <details key={note.excerpt} className="sentence-note">
              <summary>长难句拆解</summary>
              <blockquote lang="en" data-lookup-text={onLookupSelection ? 'true' : undefined}>{note.excerpt}</blockquote>
              <dl>
                <div><dt>译文</dt><dd>{note.translationZh}</dd></div>
                <div><dt>结构</dt><dd>{note.structureZh}</dd></div>
                <div><dt>用法</dt><dd>{note.usageZh}</dd></div>
              </dl>
            </details>
          ))}
          <details className="weekend-seed">
            <summary>保存给周末的输出练习</summary>
            <p lang="en" data-lookup-text={onLookupSelection ? 'true' : undefined}>{item.languageAid.weekendPracticeSeed}</p>
          </details>
        </section>
      )}

      <section className="discussion-card">
        <p className="eyebrow">Discuss with Codex</p>
        <h3>{item.discussion.prompt}</h3>
        <p>{item.discussion.context}</p>
      </section>

      <div className="article-actions" aria-label="文章操作">
        <button type="button" aria-pressed={state?.saved ?? false} className={state?.saved ? 'is-selected' : ''} onClick={() => onSaved(!(state?.saved ?? false))}>{state?.saved ? '已收藏' : '收藏'}</button>
        <button type="button" aria-pressed={state?.interest === 'interested'} className={state?.interest === 'interested' ? 'is-selected' : ''} onClick={() => onInterest(state?.interest === 'interested' ? null : 'interested')}>感兴趣</button>
        <button type="button" aria-pressed={state?.interest === 'not_interested'} className={state?.interest === 'not_interested' ? 'is-negative' : ''} onClick={() => onInterest(state?.interest === 'not_interested' ? null : 'not_interested')}>不感兴趣</button>
        <button type="button" className="article-actions__discuss" onClick={onDiscuss}>与 Codex 讨论</button>
      </div>

      <a className="source-button" href={item.source.url} target="_blank" rel="noreferrer noopener">阅读来源原文 <span aria-hidden="true">↗</span></a>
      <SelectionLookupButton selection={selection} onLookup={lookup} />
    </article>
  )
}
